import { withRouter, Redirect } from 'react-router-dom'
import React from 'react'
import { connect } from 'react-redux'
import {
  getOperation,
  startOperation,
  pauseOperation,
  reportFailure,
  resumeOperation,
  getStopReasons,
  finishOperation,
  returnToOperations
} from '../actions/ProductionActions'
import StopReasonPopup from './StopReasonPopup'
import TallyForm from './TallyForm'
import Done from './Done'

const mapStateToProps = state => ({
  authenticated: state.auth.session.token !== false,
  operationId: state.router.location.query.operation
    ? parseInt(state.router.location.query.operation, 10) : false,
  operation: state.production.operation,
  workcenter_name: state.config.workcenter_name,
  stopReasons: state.production.stopReasons
})

const mapDispatchToProps = dispatch => ({
  getOperationAction: (id) => dispatch(getOperation(id)),
  startOperationAction: (id) => dispatch(startOperation(id)),
  pauseOperationAction: (id, lossId, note) => dispatch(pauseOperation(id, lossId, note)),
  reportFailureAction: (id, lossId, note) => dispatch(reportFailure(id, lossId, note)),
  resumeOperationAction: (id) => dispatch(resumeOperation(id)),
  getStopReasonsAction: (eventType) => dispatch(getStopReasons(eventType)),
  finishOperationAction: (id, qtyProduced, qtyWaste, lotName) =>
    dispatch(finishOperation(id, qtyProduced, qtyWaste, lotName)),
  returnToOperationsAction: (productionId) => dispatch(returnToOperations(productionId))
})

function formatElapsed(operation) {
  let diff = Math.round((operation.productive_duration || 0) * 60)
  if (operation.running_since) {
    const start = new Date(operation.running_since.replace(' ', 'T') + 'Z')
    diff += Math.max(0, Math.floor((Date.now() - start.getTime()) / 1000))
  }
  const h = String(Math.floor(diff / 3600)).padStart(2, '0')
  const m = String(Math.floor((diff % 3600) / 60)).padStart(2, '0')
  const s = String(diff % 60).padStart(2, '0')
  return `${h}:${m}:${s}`
}

class OperationViewComponent extends React.Component {
  constructor(props) {
    super(props)
    this.state = { popup: false, showTally: false, elapsed: '00:00:00' }
  }

  componentDidMount() {
    const { operation, operationId, getOperationAction } = this.props
    if (operationId && (!operation || operation.id !== operationId)) {
      getOperationAction(operationId)
    }
    this.tick = setInterval(this.updateElapsed, 1000)
    this.updateElapsed()
  }

  componentDidUpdate() {
    this.updateElapsed()
  }

  componentWillUnmount() {
    clearInterval(this.tick)
  }

  updateElapsed = () => {
    const { operation } = this.props
    if (operation && operation.state === 'progress' && !operation.is_blocked && operation.is_running) {
      const elapsed = formatElapsed(operation)
      if (elapsed !== this.state.elapsed) this.setState({ elapsed })
    }
  }

  openReasonPopup = (eventType) => {
    this.props.getStopReasonsAction(eventType)
    this.setState({ popup: eventType })
  }

  closeReasonPopup = () => this.setState({ popup: false })

  confirmReason = (lossId, note) => {
    const { operation, pauseOperationAction, reportFailureAction } = this.props
    if (this.state.popup === 'break') {
      pauseOperationAction(operation.id, lossId, note)
    } else {
      reportFailureAction(operation.id, lossId, note)
    }
    this.setState({ popup: false })
  }

  goBack = () => {
    const { operation, returnToOperationsAction } = this.props
    returnToOperationsAction(operation.production_id)
  }

  render() {
    const { authenticated, operation, workcenter_name, stopReasons,
      startOperationAction, resumeOperationAction, finishOperationAction } = this.props
    if (!authenticated) return <Redirect to='/login' />
    if (!operation) return null
    if (operation.state === 'done') {
      return <Done operation={operation} onReturn={this.goBack} />
    }

    const { popup, showTally, elapsed } = this.state

    if (showTally) {
      return (
        <TallyForm
          operation={operation}
          onCancel={() => this.setState({ showTally: false })}
          onSubmit={(qtyProduced, qtyWaste, lotName) =>
            finishOperationAction(operation.id, qtyProduced, qtyWaste, lotName).then(result => {
              if (result.state === 'done') {
                this.setState({ showTally: false })
              } else {
                this.goBack()
              }
            }).catch(() => {})}
        />
      )
    }

    return (
      <>
        <div className="op-toolbar">
          <button type="button" className="secondary" onClick={this.goBack}>‹ Wstecz</button>
          <div className="op-timer">
            <span>Czas pracy</span>
            <b>{operation.state === 'progress' && !operation.is_blocked && operation.is_running ? elapsed : '—'}</b>
          </div>
          {operation.state === 'progress' && (
            <button type="button" onClick={() => this.setState({ showTally: true })}>
              Zakończ operację
            </button>
          )}
        </div>

        <h2>{operation.name}</h2>
        <p className="sub">
          Zlecenie {operation.production_name} · {operation.product_name}
        </p>

        {!operation.materials_available && (
          <div className="op-warning">
            ⚠ Brakuje surowca: {(operation.missing_materials || []).join(', ')} — sprawdź stan magazynowy.
          </div>
        )}

        <div className="op-grid">
          <div className="ramka">
            <h4>Operacja: {operation.name}</h4>
            <div className="f_group"><label>Zlecenie</label><div>{operation.production_name}</div></div>
            <div className="f_group"><label>Produkt</label><div>{operation.product_name}</div></div>
            <div className="f_group"><label>Zaplanowano</label><div>{operation.qty_production} szt.</div></div>
            <div className="f_group"><label>Wykonano do teraz</label><div>{operation.qty_produced} szt.</div></div>
            <div className="f_group"><label>Pozostało do wykonania</label>
              <div>{Math.max(0, operation.qty_limit - operation.qty_produced - operation.qty_waste)} szt.</div>
            </div>
            <div className="f_group"><label>Stanowisko</label><div>{workcenter_name}</div></div>
            {operation.operation_note && (
              <div className="op-note">{operation.operation_note}</div>
            )}
          </div>
          <div>
            <div className="op-actions">
              {operation.state === 'ready' && (
                <button type="button" className="green" onClick={() => startOperationAction(operation.id)}>
                  ▶ Start
                </button>
              )}
              {operation.state === 'progress' && operation.is_blocked && (
                <button type="button" className="green" onClick={() => resumeOperationAction(operation.id)}>
                  ▶ Wznów
                </button>
              )}
              {operation.state === 'progress' && !operation.is_blocked && !operation.is_running && (
                <button type="button" className="green" onClick={() => startOperationAction(operation.id)}>
                  ▶ Wznów
                </button>
              )}
              {operation.state === 'progress' && !operation.is_blocked && operation.is_running && (
                <>
                  <button type="button" className="secondary" onClick={() => this.openReasonPopup('break')}>
                    ⏸ Przerwa
                  </button>
                  <button type="button" className="red" onClick={() => this.openReasonPopup('failure')}>
                    ⚠ Awaria
                  </button>
                </>
              )}
            </div>
            <p className="op-state">
              Stan: <b>{operation.panel_display_state === 'break'
                ? 'PRZERWA'
                : operation.panel_display_state === 'failure'
                ? 'AWARIA'
                : operation.is_blocked
                ? 'ZATRZYMANA'
                : (operation.state === 'progress'
                  ? (operation.is_running ? 'W TRAKCIE' : 'WSTRZYMANA (do wznowienia)')
                  : 'GOTOWA DO STARTU')}</b>
            </p>
          </div>
        </div>

        {popup && (
          <StopReasonPopup
            eventType={popup}
            reasons={stopReasons[popup] || []}
            onCancel={this.closeReasonPopup}
            onConfirm={this.confirmReason}
          />
        )}
      </>
    )
  }
}

const OperationView = connect(mapStateToProps, mapDispatchToProps)(OperationViewComponent)
export default withRouter(OperationView)
